import { useEffect } from 'react'
import { BlogLanguageSwitcher, type BlogLocale } from '@saas-pages/shared/public/blog'
import { Seo, buildBlogPageMetadata } from '@saas-pages/shared/public/seo'
import branding from '../../../branding.config'
import { getAllPosts, getPostBySlug, type BlogPost } from '@/shared/blog/posts'
import { useLocale } from '@/shared/i18n'
import { mediatikUtilityRegistry, type RouteKey } from '@/shared/utility-registry'

const COPY = {
  ru: {
    breadcrumb: 'Блог',
    languageAria: 'Язык статьи',
    localeName: { ru: 'Русский', en: 'English' },
    faq: 'Вопросы',
    relatedTools: 'Инструменты по теме',
    allPosts: 'Все записи блога',
    notFoundTitle: 'Статья не найдена',
    notFoundDescription: 'Проверьте адрес или вернитесь к списку статей.',
  },
  en: {
    breadcrumb: 'Blog',
    languageAria: 'Article language',
    localeName: { ru: 'Русский', en: 'English' },
    faq: 'Questions',
    relatedTools: 'Related tools',
    allPosts: 'All blog posts',
    notFoundTitle: 'Article not found',
    notFoundDescription: 'Check the address or return to the article list.',
  },
} satisfies Record<BlogLocale, {
  breadcrumb: string
  languageAria: string
  localeName: Record<BlogLocale, string>
  faq: string
  relatedTools: string
  allPosts: string
  notFoundTitle: string
  notFoundDescription: string
}>

const EN_TOOL_COPY: Partial<Record<RouteKey, { title: string; description: string }>> = {
  'voice-recorder': {
    title: 'Online voice recorder',
    description: 'Record your microphone, listen to the result, and download the browser-selected format.',
  },
}

function formatDate(iso: string, locale: BlogLocale): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString(locale === 'en' ? 'en-GB' : 'ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

function localeHref(post: BlogPost): Partial<Record<BlogLocale, string>> {
  return {
    [post.locale]: `/blog/${post.slug}`,
    ...Object.fromEntries(
      Object.entries(post.translations)
        .filter((entry): entry is [BlogLocale, string] => Boolean(entry[1]))
        .map(([locale, translatedSlug]) => [locale, `/blog/${translatedSlug}`]),
    ),
  }
}

export function BlogPostPage({ slug }: { slug: string }) {
  const post = getPostBySlug(slug)
  const locale = post?.locale ?? 'ru'
  const copy = COPY[locale]
  const { setRouteLocale } = useLocale()

  useEffect(() => {
    if (!post) return
    setRouteLocale(post.locale)
    return () => setRouteLocale(null)
  }, [post, setRouteLocale])

  if (!post) {
    return (
      <>
        <Seo
          title={`${copy.notFoundTitle} - mediatik.ru`}
          description={copy.notFoundDescription}
          canonical={`https://${branding.domain}/blog/${slug}`}
          noindex
        />
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="text-3xl font-bold tracking-normal">{copy.notFoundTitle}</h1>
          <p className="mt-4 text-muted-foreground">{copy.notFoundDescription}</p>
          <a href="/blog" className="mt-6 inline-block text-primary hover:underline">
            {copy.allPosts}
          </a>
        </div>
      </>
    )
  }

  const metadata = buildBlogPageMetadata(post, getAllPosts(), {
    domain: branding.domain,
    brandName: branding.domain,
    defaultOgImage: branding.ogImage,
  })
  const PostContent = post.Component
  const relatedTools = post.relatedTools.flatMap((key) => {
    const route = mediatikUtilityRegistry.find((item) => item.routeKey === key)
    return route ? [route] : []
  })

  return (
    <>
      <Seo metadata={metadata} />

      <article className="mx-auto max-w-3xl">
        <nav aria-label="mediatik.ru" className="mb-6 text-sm text-muted-foreground">
          <a href="/" className="hover:text-foreground">mediatik.ru</a>
          <span className="mx-2">/</span>
          <a href="/blog" className="hover:text-foreground">{copy.breadcrumb}</a>
          <span className="mx-2">/</span>
          <span className="text-foreground">{post.title}</span>
        </nav>

        <header className="mb-8">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md border bg-card px-2 py-0.5">
                {copy.localeName[post.locale]}
              </span>
              <time dateTime={post.date}>{formatDate(post.date, post.locale)}</time>
            </div>
            <BlogLanguageSwitcher
              currentLocale={post.locale}
              ariaLabel={copy.languageAria}
              labels={copy.localeName}
              hrefs={localeHref(post)}
              disableMissingHrefs
              onSelectLocale={setRouteLocale}
            />
          </div>
          <h1 className="text-3xl font-bold tracking-normal sm:text-4xl">{post.title}</h1>
          <p className="mt-4 leading-7 text-muted-foreground">{post.description}</p>
        </header>

        <div className="prose prose-slate max-w-none prose-headings:tracking-normal prose-headings:text-foreground prose-p:text-foreground/90 prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground prose-code:rounded prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none dark:prose-invert">
          <PostContent />
        </div>

        {post.faq.length > 0 && (
          <section className="mt-12 border-t pt-8">
            <h2 className="text-xl font-semibold tracking-normal">{copy.faq}</h2>
            <div className="mt-4 space-y-4">
              {post.faq.map((item) => (
                <div key={item.q}>
                  <h3 className="font-medium tracking-normal">{item.q}</h3>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">{item.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {relatedTools.length > 0 && (
          <section className="mt-12 rounded-lg border border-rose-200/80 bg-rose-50/50 p-6 dark:border-rose-900/60 dark:bg-rose-950/20">
            <h2 className="mb-4 text-base font-semibold tracking-normal">{copy.relatedTools}</h2>
            <ul className="space-y-3">
              {relatedTools.map((tool) => {
                const translated = post.locale === 'en' ? EN_TOOL_COPY[tool.routeKey] : undefined
                return (
                  <li key={tool.routeKey}>
                    <a href={tool.path} className="block text-sm hover:text-primary">
                      <span className="font-medium">{translated?.title ?? tool.title}</span>
                      <span className="block text-xs leading-5 text-muted-foreground">
                        {translated?.description ?? tool.description}
                      </span>
                    </a>
                  </li>
                )
              })}
            </ul>
          </section>
        )}

        <div className="mt-10 text-sm">
          <a href="/blog" className="text-primary hover:underline">{copy.allPosts}</a>
        </div>
      </article>
    </>
  )
}
